/**
 * <Grid> — CSS-grid container.
 *
 * Wraps the auto-fit / fixed-column patterns dashboards use most.
 * Skip Tailwind arbitrary values like `grid-cols-[1fr_2fr]` — they're
 * the legitimate escape hatch but go through Grid first.
 *
 * Spec: docs/design-system/03-spacing-layout.md
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import type { StackGap } from './Stack';

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Column count. Number for fixed columns at every breakpoint,
   * or an object keyed by breakpoint for responsive (e.g. `{ base: 1, md: 2, lg: 3 }`).
   */
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 12 | { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
  /**
   * Auto-fit columns at a minimum size. Useful for KPI tiles and card galleries.
   * Mutually exclusive with `cols` — auto-fit wins if both supplied.
   */
  minColWidth?: string;
  gap?: StackGap;
  children: ReactNode;
}

const COL_CLASSES: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  12: 'grid-cols-12',
};

const RESP_PREFIX = { base: '', sm: 'sm:', md: 'md:', lg: 'lg:', xl: 'xl:' } as const;

const GAP_CLASSES: Record<StackGap, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-12',
};

function colsToClassName(cols: GridProps['cols']): string {
  if (typeof cols === 'number') return COL_CLASSES[cols];
  if (typeof cols === 'object' && cols !== null) {
    return Object.entries(cols)
      .map(([bp, n]) => {
        if (typeof n !== 'number') return '';
        const prefix = RESP_PREFIX[bp as keyof typeof RESP_PREFIX] ?? '';
        return `${prefix}${COL_CLASSES[n]}`;
      })
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  { cols, minColWidth, gap = 'md', className, children, style, ...rest },
  ref,
) {
  const useAutoFit = !!minColWidth;
  return (
    <div
      ref={ref}
      className={cn('grid', !useAutoFit && cols !== undefined && colsToClassName(cols), GAP_CLASSES[gap], className)}
      style={useAutoFit ? { gridTemplateColumns: `repeat(auto-fit, minmax(${minColWidth}, 1fr))`, ...style } : style}
      {...rest}
    >
      {children}
    </div>
  );
});

Grid.displayName = 'Grid';

export default Grid;
